import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { TWIST_GENERATOR_OPTIONS } from '@/pages/Onboarding/options/TwistGenerator/TWIST_GENERATOR_OPTIONS';
import {
  TwistGeneratorOptionsType,
  TwistGeneratorOptionIdType,
} from '@/pages/Onboarding/options/TwistGenerator/types';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataType } from '../../types';

export const ScreenTwistGenerator: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'TwistGenerator',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const twistGenerator = form.watch('twist_generator');
  const gender = form.watch('gender');

  const getGenderText = () => {
    switch (gender) {
      case 'female':
        return 'She';
      case 'male':
        return 'He';
      default:
        return 'They';
    }
  };

  const getDynamicOptions = (): TwistGeneratorOptionsType => {
    return TWIST_GENERATOR_OPTIONS.map(
      (option: OnbOptionType<TwistGeneratorOptionIdType>) => {
        if (option.id === 'already_in_relationship') {
          return {
            ...option,
            title: `${getGenderText()} is already in a relationship 👀`,
          };
        }

        return option;
      },
    );
  };

  const handleChange = (
    option: OnbOptionType<LayoutDataType['twist_generator']>,
  ) => {
    userService.createOrUpdate({
      onboarding_data: {
        twistGenerator: option.title,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  return (
    <OnboardingPage>
      <OnboardingNavigation isRenderBackButton={true} />

      <IonContent>
        <OwnAiTitle>Add a little spice. Choose a twist.</OwnAiTitle>
        <OnboardingOptionsList>
          {getDynamicOptions().map((option) => (
            <OnboardingOptionItem
              checked={twistGenerator === option.id}
              control={form.control}
              option={option}
              name="twist_generator"
              key={option.id}
              onChange={() => handleChange(option)}
              TEST_ID={`twist-generator-${option.id}`}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>
    </OnboardingPage>
  );
};
